import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { GcmUserPermission } from "userful-chronos-app-common-js/dist/models/gcm-user-permission";
import { UserfulSearchBar } from "userful-chronos-common-ui/dist";
import { ClickAndDragZone } from "userful-chronos-common-ui/dist/elements";
import UserfulTable from "../../common/Table2026/UserfulTable2026";
import { BackupData, IConstellationInfo } from "../../model/CarbonExternalModels";
import {
    useCarbonDispatch,
    useCarbonSelector,
    selectSearchInput,
    selectBackupAndRestoreFilters,
    selectBackups,
} from "../../store/carbonStore";
import { filterBackupAndRestore } from "../../store/filters/filtersManager";
import { FiltersActions } from "../../store/filters/filtersSlice";
import { convertIBackupAndRestoreToTable } from "../../common/components/UserfulTable/UserfulTableConverter";
import EmptyPlaceholder from "../../common/components/placeholders/EmptyPlaceholder";
import CreateBackupAndRestore from "./CreateBackupAndRestore";
import { InfoBanner } from "userful-chronos-common-ui/dist/widgets";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

interface IProps {
    constellations: { [id: string]: IConstellationInfo };
}

export default function BackupAndRestoreTable(props: IProps) {
    const dispatch = useCarbonDispatch();
    const ZONE_ID = "backup-restore-table";

    const permissions: GcmUserPermission = getGlobalStates().gcmUserPermission;

    // Filtering
    const backupAndRestoreFilters = useCarbonSelector(selectBackupAndRestoreFilters);
    const searchInput = useCarbonSelector(selectSearchInput);

    // Data
    const allBackupAndRestore: BackupData[] = useCarbonSelector(selectBackups);
    const atLeastOneBackupWarning = allBackupAndRestore.some(
        (backup) => backup.status === "UNEXPECTED_PROCESSING_DELAY",
    );

    const filteredBackupAndRestore: BackupData[] = filterBackupAndRestore(
        backupAndRestoreFilters,
        searchInput.backupAndRestore,
        allBackupAndRestore,
    );
    const table = convertIBackupAndRestoreToTable(filteredBackupAndRestore);

    // Functions
    const onSearchBarChange = function (input: string) {
        dispatch(FiltersActions.setSearchInput({ ...searchInput, backupAndRestore: input }));
    };

    // Renders
    const insertBody = () => {
        if (allBackupAndRestore.length > 0) {
            if (filteredBackupAndRestore.length > 0) {
                return null;
            } else {
                return (
                    <EmptyPlaceholder
                        img="./images/notFound.png"
                        title={"No Backups Found"}
                        description={"Please refine your search/filter criteria and try again."}
                    />
                );
            }
        } else {
            return (
                <EmptyPlaceholder
                    img="./images/notFound.png"
                    title={"No Backups Available"}
                    description={"There are no available backups, click Create New Backup button."}
                    additionalComponents={<CreateBackupAndRestore type={"SECONDARY"} backups={allBackupAndRestore} />}
                />
            );
        }
    };

    if (!permissions.isConstellationViewer) return null;

    return (
        <div className="gcm-contents-wrapper">
            <div className="gcm-contents-header-wrapper">
                <div className="gcm-contents-sub-header-wrapper gcm-contents-sub-header-priority-wrapper">
                    <UserfulSearchBar forceValue={searchInput.clusters} onChange={onSearchBarChange} />
                </div>
                <div className="gcm-contents-sub-header-wrapper">
                    <CreateBackupAndRestore type={"PRIMARY"} backups={allBackupAndRestore} />
                </div>
            </div>
            <div className="gcm-contents-body-wrapper">
                {atLeastOneBackupWarning && (
                    <InfoBanner
                        component={<FontAwesomeIcon icon={faTriangleExclamation} />}
                        descriptions={[
                            <p key="1">
                                At least one of your backups has taken longer than 60 minutes to complete. This is
                                significantly longer than expected.
                            </p>,
                            <p key="2">
                                For more details, contact the administrator of that system or inspect the system logs.
                                Alternatively, you may remove the backup and try again.
                            </p>,
                        ]}
                    ></InfoBanner>
                )}
                {allBackupAndRestore.length === 0 && insertBody()}
                {allBackupAndRestore.length > 0 && (
                    <ClickAndDragZone id={ZONE_ID}>
                        {filteredBackupAndRestore.length > 0 ? (
                            <UserfulTable table={table} selection={false} />
                        ) : (
                            insertBody()
                        )}
                    </ClickAndDragZone>
                )}
            </div>
        </div>
    );
}
